/**
 * The Systems menu at the top of the desk (Jeff, 2026-10-25): every piece of plumbing as a dot and
 * a word, and one line for the strip that says "All systems go" when nothing needs a look. Pure.
 *
 * Dialpad goes quiet on its own every night and weekend, so silence only counts against it inside
 * support hours, and only once two hours of those hours pass with nothing (the old 24 hour rule
 * turned it amber every Sunday while the word still said "Arriving").
 */
import { type OverviewOutput } from '@lsc/contract';
import { type LiveState, type LiveStatus } from './live';
import { ageWords } from './overview-copy';

export type Tone = 'live' | 'attention' | 'alert' | 'quiet';
export interface SystemRow {
  label: string;
  tone: Tone;
  word: string;
  when: string | null;
}

/** Support hours in New York: weekdays, 9 AM to 6 PM. */
export const SUPPORT_HOURS = { days: [1, 2, 3, 4, 5], open: 9, close: 18 } as const;
const QUIET_LIMIT_H = 2;
const TZ = 'America/New_York';

function nyParts(d: Date): { weekday: number; hour: number; minute: number } {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
      .formatToParts(d)
      .map((x) => [x.type, x.value]),
  );
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(p['weekday'] ?? '');
  return { weekday, hour: Number(p['hour']), minute: Number(p['minute']) };
}

/** Hours since support opened today, or null outside support hours. */
export function hoursIntoSupportDay(now: Date): number | null {
  const { weekday, hour, minute } = nyParts(now);
  if (!(SUPPORT_HOURS.days as readonly number[]).includes(weekday)) return null;
  const h = hour + minute / 60;
  if (h < SUPPORT_HOURS.open || h >= SUPPORT_HOURS.close) return null;
  return h - SUPPORT_HOURS.open;
}

/** "Fri 3:45 PM" in New York, the last time something arrived. */
export function lastWords(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { timeZone: TZ, weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

export function dialpadRow(last: string | null, now: Date): SystemRow {
  const label = 'Dialpad events';
  if (!last) return { label, tone: 'quiet', word: 'No events yet', when: null };
  const sinceH = (now.getTime() - new Date(last).getTime()) / 3600_000;
  const when = `last ${lastWords(last)}`;
  if (sinceH < QUIET_LIMIT_H) return { label, tone: 'live', word: 'Arriving', when: `last ${ageWords(last, now) ?? ''} ago` };
  const into = hoursIntoSupportDay(now);
  if (into === null) return { label, tone: 'quiet', word: 'Quiet, outside support hours', when };
  // Inside hours: silence counts from the later of the last event and this morning's opening.
  if (Math.min(sinceH, into) >= QUIET_LIMIT_H) return { label, tone: 'attention', word: 'Nothing in support hours for over 2 hours', when };
  return { label, tone: 'quiet', word: 'Quiet so far today', when };
}

export function callsRow(status: LiveStatus, calls: LiveState['calls']): SystemRow {
  const label = 'Calls to this desk';
  if (status === 'connecting') return { label, tone: 'quiet', word: 'Connecting', when: null };
  if (status !== 'live') return { label, tone: 'alert', word: 'Not connected, reconnecting', when: null };
  return calls === 'ok' ? { label, tone: 'live', word: 'Connected', when: null } : { label, tone: 'attention', word: 'Delayed', when: null };
}

export function systemRows(s: OverviewOutput['systems'], ai: OverviewOutput['ai'], now: Date): SystemRow[] {
  const aiFailed = ai.lastFailedAt !== null && (!ai.lastRunAt || ai.lastFailedAt > ai.lastRunAt);
  return [
    { label: 'Background worker', tone: s.worker === 'ok' ? 'live' : s.worker === 'down' ? 'alert' : 'quiet', word: s.worker === 'ok' ? 'Running' : s.worker === 'down' ? 'Not running' : 'Has not started', when: s.workerBeatAt ? `checked in ${ageWords(s.workerBeatAt, now) ?? ''} ago` : null },
    dialpadRow(s.dialpadLastEventAt, now),
    { label: 'Slack posts', tone: s.slackBacklog > 5 ? 'attention' : 'live', word: s.slackBacklog === 0 ? 'Caught up' : `${String(s.slackBacklog)} waiting`, when: s.slackLastPostAt ? `last post ${ageWords(s.slackLastPostAt, now) ?? ''} ago` : null },
    { label: 'Slack list mirror', tone: s.mirrorLastOk === null ? 'quiet' : s.mirrorLastOk ? 'live' : 'alert', word: s.mirrorLastOk === null ? 'Never ran' : s.mirrorLastOk ? 'Working' : 'Failing', when: s.mirrorLastAt ? `last pass ${ageWords(s.mirrorLastAt, now) ?? ''} ago` : null },
    { label: 'AI summaries', tone: !ai.on ? 'quiet' : aiFailed ? 'alert' : 'live', word: !ai.on ? 'Not set up' : aiFailed ? 'Last run failed' : 'Working', when: ai.lastRunAt ? `last run ${ageWords(ai.lastRunAt, now) ?? ''} ago` : null },
  ];
}

/**
 * The strip's one line. A dropped socket keeps its own words (a rep must know calls are not
 * reaching them); otherwise the worst piece names itself, and when nothing needs a look it is
 * "All systems go". Quiet is not a problem.
 */
export function systemsSummary(rows: SystemRow[]): { tone: Tone; words: string } {
  const calls = rows.find((r) => r.label === 'Calls to this desk');
  if (calls?.tone === 'alert') return { tone: 'alert', words: 'Calls not connected, reconnecting' };
  for (const tone of ['alert', 'attention'] as const) {
    const bad = rows.filter((r) => r.tone === tone);
    if (bad.length === 1 && bad[0]) return { tone, words: `${bad[0].label}: ${bad[0].word.charAt(0).toLowerCase()}${bad[0].word.slice(1)}` };
    if (bad.length > 1) return { tone, words: `${String(bad.length)} systems need a look` };
  }
  return { tone: 'live', words: 'All systems go' };
}

export const TONE_DOT: Record<Tone, string> = { live: 'bg-live', attention: 'bg-attention', alert: 'bg-alert', quiet: 'bg-line-strong' };
export const TONE_TEXT: Record<Tone, string> = { live: 'text-ink-2', attention: 'text-attention', alert: 'text-alert', quiet: 'text-ink-2' };
